import React, { useState, useEffect} from "react";
import { useSelector } from "react-redux";

import {fetchRiskLosses as fetchRiskLossesService,fetchRiskScenarios as fetchRiskScenariosService,fetchControlStrength as fetchControlStrengthService,saveRiskScenarioLosses as saveRiskScenarioLossesService,analysis as analysisService } from "services/modules/crq/riskquantservice";

import {
  Autocomplete,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  TextField,
  Button,
  Dialog,
  Divider,
  DialogActions,
  DialogContent,
  DialogTitle,
  OutlinedInput
} from "@mui/material";

import { Tab as BootstrapTab, Tabs as BootstrapTabs} from "react-bootstrap";
import DeleteIcon from "@mui/icons-material/Delete";
import AddBoxIcon from "@mui/icons-material/AddBox";

import { useNotification } from "config/Notification";

const RiskQuantPage = () => {
  // Notification
  const { openNotification } = useNotification();
  
  // Redux selectors
  const companyData = useSelector((state) => state.auth.companyData);
  
  // State Variables
  const [formData, setFormData] = useState({
    risklabel: "",
    risklabelid: "",
    risklosslist: [
      {
        riskprimaryloss: "",
        minploss: "",
        maxploss: "",
        risksecondaryloss: "",
        minsloss: "",
        maxsloss: "",
      },
    ],
    minfreq: "",
    maxfreq: "",
    mof: "",
    role: "",
  });
  const [controlStrength, setControlStrength] = useState(50);
  const [riskScenarios, setRiskScenarios] = useState([]);
  const [riskScenarioSecondaryLosses, setRiskScenarioSecondaryLosses] = useState([]);
  const [selectedTab, setSelectedTab] = useState("1");
  const [selectedRiskScenario, setSelectedRiskScenario] = useState("");
  const [riskScenarioPrimaryLosses, setRiskScenarioPrimaryLosses] = useState([]);
  const [primaryPopupOpen, setPrimaryPopupOpen] = useState(false);
  const [secondaryPopupOpen, setSecondaryPopupOpen] = useState(false);
  const [newPrimaryLossType, setNewPrimaryLossType] = useState("");
  const [newSecondaryLossType, setNewSecondaryLossType] = useState("");
  const [minFreqError, setMinFreqError] = useState(false);
  const [maxFreqError, setMaxFreqError] = useState(false);
  const [mostLikelyFreqError, setMostLikelyFreqError] = useState(false);
  const [primaryLossError, setPrimaryLossError] = useState(false);
  const [secondaryLossError, setSecondaryLossError] = useState(false);
  const [lossCards, setLossCards] = useState([
    {
      id: 1,
      primaryLoss: null,
      secondaryLoss: null,
      minploss: "",
      maxploss: "",
      minsloss: "",
      maxsloss: "",
    },
  ]);
  const [lossCardsErrors, setLossCardsErrors] = useState(
    lossCards.map(() => ({
      minPLossError: false,
      maxPLossError: false,
      minSLossError: false,
      maxPLossValueError: false,
      minPLossValueError: false,
      maxSLossValueError: false,
      minSLossValueError: false,
      maxSLossError: false,
      primaryLossError: false,
      secondaryLossError: false,
    }))
  );
  const [controlStrengthError, setControlStrengthError] = useState("");
  
  // useEffect fetches risk scenarios data
  useEffect(() => {
    const fetchRiskScenariosData = async () => {
      try {
        const response = await fetchRiskScenariosService();
        setRiskScenarios(response?.data);
        if (response?.data?.length > 0) {
          setSelectedRiskScenario(response.data[0]);
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchRiskScenariosData();
  }, []);
  
  // useEffect fetches control strength data based on company id
  useEffect(() => {
    const getControlStrengthData = async () => {
      try {
        const response = await fetchControlStrengthService({company: companyData?.companyid});
        if (response.status === 200) {
          const controlStrengthValue = response.data.ControlStrength.toFixed(0);
          setControlStrength(
            controlStrengthValue === "0" ? "50" : controlStrengthValue
          );
        }
      } catch (error) {
      }
    };
    if(companyData?.companyid !== null)
    getControlStrengthData();
  }, [companyData]);
 
  // useEffect fetches primary loss types data based on selected risk scenario and company
  useEffect(() => {
    if (selectedRiskScenario?.id && companyData?.companyid !== null) {
      const fetchPrimaryRiskLossesData = async () => {
        try {
          const params = {riskscenario_id: selectedRiskScenario?.id,loss_type: "Primary Loss",company: companyData?.companyid};
          const response = await fetchRiskLossesService(params)
          setRiskScenarioPrimaryLosses(response.data.result);
        } catch (error) {
          openNotification('error','error')
        }
      };
      fetchPrimaryRiskLossesData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRiskScenario.id,companyData.companyid]);

  // useEffect fetches secondary loss types data based on selected risk scenario and company
  useEffect(() => {
    if (selectedRiskScenario?.id && companyData?.companyid !== null) {
      const params = {riskscenario_id: selectedRiskScenario.id,loss_type: "Secondary Loss",company: companyData.companyid };
      const fetchSecondaryRiskLossesData = async () => {
        try {
          const response = await fetchRiskLossesService(params);
          setRiskScenarioSecondaryLosses(response.data.result);
        } catch (error) {
          openNotification('error','error')
        }
      };
      fetchSecondaryRiskLossesData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRiskScenario.id,companyData.companyid]);
  
  // Formats risk data
  const formatRiskData = (value) => {
    return value.replace(/,/g, "");
  };
  
  // Handles form data submission in the last step
  const handleAnalysis = async () => {
    try {
      const minFrequency = parseFloat(formatRiskData(formData.minfreq));
      const maxFrequency = parseFloat(formatRiskData(formData.maxfreq));
      const modeFrequency = parseFloat(formatRiskData(formData.mof));
      const primaryLossData = lossCards.map((card) => {
        const primaryLossItem = riskScenarioPrimaryLosses.find(
          (loss) => loss.risk_loss === card.primaryLoss
        );
        return {
          id: primaryLossItem ? primaryLossItem.id : "",
          minPrimaryLoss: parseFloat(formatRiskData(card.minploss)),
          maxPrimaryLoss: parseFloat(formatRiskData(card.maxploss)),
        };
      });
      const secondaryLossData = lossCards.map((card) => {
        const secondaryLossItem = riskScenarioSecondaryLosses.find(
          (loss) => loss.risk_loss === card.secondaryLoss
        );
        return {
          id: secondaryLossItem ? secondaryLossItem.id : "",
          minSecondaryLoss: parseFloat(formatRiskData(card.minsloss)),
          maxSecondaryLoss: parseFloat(formatRiskData(card.maxsloss)),
        };
      });
      const postData = {
        controlStrength,
        minFrequency,
        maxFrequency,
        modeFrequency,
        primaryloss: primaryLossData,
        risklabel: selectedRiskScenario.RiskScenarios,
        risklabelid: selectedRiskScenario.id,
        secondaryloss: secondaryLossData,
      };
      const response = await analysisService({
        objformdata: postData,
      });
      if (response.data.message === "Successfully inserted") {
        window.location.reload();
        setFormData({
          risklabel: "",
          risklabelid: "",
          risklosslist: [
            {
              riskprimaryloss: "",
              minploss: "",
              maxploss: "",
              risksecondaryloss: "",
              minsloss: "",
              maxsloss: "",
            },
          ],
          minfreq: "",
          maxfreq: "",
          mof: "",
          role: "",
        });
      }
      openNotification(response.data.message);
    } catch (error) {
      openNotification('error','error');
    }
  };
  
  // Adds a new secondary loss to the existing set of secondary losses for a specific risk scenario and company
  const handleAddSecondaryLoss = async () => {
    try {
      const response = await saveRiskScenarioLossesService({
        loss_type: "Secondary Loss",
        risk_loss: newSecondaryLossType,
        riskscenario_id: selectedRiskScenario.id,
        company: companyData.companyid,
      });
      const params = {loss_type: "Secondary Loss",riskscenario_id: selectedRiskScenario.id,company: companyData.companyid}
      if (response?.status === 200 || response?.status === 201) {
        openNotification("Risk scenario loss type saved successfully","success");
        const updatedResponse = await fetchRiskLossesService(params);
        setRiskScenarioSecondaryLosses(updatedResponse.data.result);
      } else {
        openNotification(response, "error");
        setPrimaryPopupOpen(false);
      }
      setSecondaryPopupOpen(false);
      setNewSecondaryLossType("");
    } catch (error) {
      openNotification('error','error')
    }
  };
  
  // Adds a new primary loss to the existing set of primary losses for a specific risk scenario and company
  const handleAddPrimaryLoss = async () => {
    try {
      const response = await saveRiskScenarioLossesService({
        riskscenario_id: selectedRiskScenario.id,
        loss_type: "Primary Loss",
        risk_loss: newPrimaryLossType,
        company: companyData.companyid,
      });
      const params = {riskscenario_id: selectedRiskScenario.id,loss_type: "Primary Loss",company: companyData.companyid}
      if (response?.status === 200 || response?.status === 201 ) {
        openNotification("Risk scenario loss type saved successfully","success");
        const updatedResponse = await fetchRiskLossesService(params);
        setRiskScenarioPrimaryLosses(updatedResponse.data.result);
      } else {
        openNotification(response, "error");
        setPrimaryPopupOpen(false);
      }
      setPrimaryPopupOpen(false);
      setNewPrimaryLossType("");
    } catch (error) {
      openNotification('server error','error')
    }
  };
  
  // Handles changes in primary losses
  const handlePrimaryLossChange = (event, value, cardId) => {
    setLossCards((prevLossCards) => {
      return prevLossCards.map((card) => {
        if (card.id === cardId) {
          return {
            ...card,
            primaryLoss: value,
          };
        }
        return card;
      });
    });
  };

  // Handle changes in secondary losses
  const handleSecondaryLossChange = (event, value, cardId) => {
    setLossCards((prevLossCards) => {
      return prevLossCards.map((card) => {
        if (card.id === cardId) {
          return {
            ...card,
            secondaryLoss: value,
          };
        }
        return card;
      });
    });
  };

  // Adds new loss card by clicking on add icon
  const addLossCard = () => {
    const newId = lossCards.length + 1;
    setLossCards((prevLossCards) => [
      ...prevLossCards,
      {
        id: newId,
        primaryLoss: "",
        secondaryLoss: "",
        minploss: "",
        maxploss: "",
        minsloss: "",
        maxsloss: "",
      },
    ]);
    setLossCardsErrors([
      ...lossCardsErrors,
      {
        minPLossError: false,
        maxPLossError: false,
        minSLossError: false,
        maxSLossError: false,
        maxPLossValueError: false,
        minPLossValueError: false,
        maxSLossValueError: false,
        minSLossValueError: false,
        primaryLossError: false,
        secondaryLossError: false,
      },
    ]);
  };
  
  // Handles maximum primary loss change
  const handleMaxPrimaryLossChange = (event, cardId) => {
    const value = event.target.value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    setLossCards(prevLossCards => {
      const newLossCards = [...prevLossCards];
      const cardIndex = cardId - 1;
      if (newLossCards[cardIndex]) {
        newLossCards[cardIndex].maxploss = value;
        const maxploss = parseFloat(value.replace(/,/g, ''));
        const minploss = parseFloat(newLossCards[cardIndex].minploss.replace(/,/g, ''));
        if (isNaN(maxploss)) {
          setLossCardsErrors(prevErrors => {
            const newErrors = [...prevErrors];
            newErrors[cardIndex].maxPLossError = true;
            return newErrors;
          });
        } else if (maxploss < minploss) {
          setLossCardsErrors(prevErrors => {
            const newErrors = [...prevErrors];
            newErrors[cardIndex].maxPLossValueError = true;
            newErrors[cardIndex].minPLossValueError = true;
            return newErrors;
          });
        } else {
          setLossCardsErrors(prevErrors => {
            const newErrors = [...prevErrors];
            newErrors[cardIndex].maxPLossValueError = false;
            newErrors[cardIndex].minPLossValueError = false;
            newErrors[cardIndex].maxPLossError = false;
            newErrors[cardIndex].minPLossError = false;
            return newErrors;
          });
        }
      }
      return newLossCards;
    });
  };
  
  // Handles minimum secondary loss change
  const handleMinSecondaryLossChange = (event, cardId) => {
    const value = event.target.value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    setLossCards(prevLossCards => {
      const newLossCards = [...prevLossCards];
      const cardIndex = cardId - 1;
      if (newLossCards[cardIndex]) {
        newLossCards[cardIndex].minsloss = value;
        const minsloss = parseFloat(value.replace(/,/g, ''));
        const maxsloss = parseFloat(newLossCards[cardIndex].maxsloss.replace(/,/g, ''));
        if (isNaN(minsloss)) {
          setLossCardsErrors(prevErrors => {
            const newErrors = [...prevErrors];
            newErrors[cardIndex].minSLossError = true;
            return newErrors;
          });
        } else if (minsloss > maxsloss) {
          setLossCardsErrors(prevErrors => {
            const newErrors = [...prevErrors];
            newErrors[cardIndex].minSLossValueError = true;
            newErrors[cardIndex].maxSLossValueError = true;
            return newErrors;
          });
        } else {
          setLossCardsErrors(prevErrors => {
            const newErrors = [...prevErrors];
            newErrors[cardIndex].minSLossValueError = false;
            newErrors[cardIndex].maxSLossValueError = false;
            newErrors[cardIndex].minSLossError = false;
            newErrors[cardIndex].maxSLossError = false;
            return newErrors;
          });
        }
      }
      return newLossCards;
    });
  };
  
  // Handles maximum secondary loss change
  const handleMaxSecondaryLossChange = (event, cardId) => {
    const value = event.target.value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    setLossCards(prevLossCards => {
      const newLossCards = [...prevLossCards];
      const cardIndex = cardId - 1;
      if (newLossCards[cardIndex]) {
        newLossCards[cardIndex].maxsloss = value;
        const maxsloss = parseFloat(value.replace(/,/g, ''));
        const minsloss = parseFloat(newLossCards[cardIndex].minsloss.replace(/,/g, ''));
        if (isNaN(maxsloss)) {
          setLossCardsErrors(prevErrors => {
            const newErrors = [...prevErrors];
            newErrors[cardIndex].maxSLossError = true;
            return newErrors;
          });
        } else if (maxsloss < minsloss) {
          setLossCardsErrors(prevErrors => {
            const newErrors = [...prevErrors];
            newErrors[cardIndex].maxSLossValueError = true;
            newErrors[cardIndex].minSLossValueError = true;
            return newErrors;
          });
        } else {
          setLossCardsErrors(prevErrors => {
            const newErrors = [...prevErrors];
            newErrors[cardIndex].maxSLossValueError = false;
            newErrors[cardIndex].minSLossValueError = false;
            newErrors[cardIndex].maxSLossError = false;
            newErrors[cardIndex].minSLossError = false;
            return newErrors;
          });
        }
      }
      return newLossCards;
    });
  };
  
  // Handles minimum primary loss change
  const handleMinPrimaryLossChange = (event, cardId) => {
    const value = event.target.value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    setLossCards(prevLossCards => {
      const newLossCards = [...prevLossCards];
      const cardIndex = cardId - 1;
      if (newLossCards[cardIndex]) {
        newLossCards[cardIndex].minploss = value;
        const minploss = parseFloat(value.replace(/,/g, ''));
        const maxploss = parseFloat(newLossCards[cardIndex].maxploss.replace(/,/g, ''));
        if (isNaN(minploss)) {
          setLossCardsErrors(prevErrors => {
            const newErrors = [...prevErrors];
            newErrors[cardIndex].minPLossError = true;
            return newErrors;
          });
        } else if (minploss > maxploss) {
          setLossCardsErrors(prevErrors => {
            const newErrors = [...prevErrors];
            newErrors[cardIndex].minPLossValueError = true;
            newErrors[cardIndex].maxPLossValueError = true;
            return newErrors;
          });
        } else {
          setLossCardsErrors(prevErrors => {
            const newErrors = [...prevErrors];
            newErrors[cardIndex].minPLossValueError = false;
            newErrors[cardIndex].maxPLossValueError = false;
            newErrors[cardIndex].minPLossError = false;
            newErrors[cardIndex].maxPLossError = false;
            return newErrors;
          });
        }
      }
      return newLossCards;
    });
  };
 

  const handleControlStrength = (event) => {
    const value = event.target.value.trim();
    setControlStrengthError(value === "");
  };
  
  // Handles field errors for all three tabs
  const handleFieldErrors = (value, setError, cardId, isPrimary) => {
    setLossCardsErrors((prevErrors) => {
      const newErrors = [...prevErrors];
      if (isPrimary) {
        newErrors[cardId - 1].primaryLossError = !value;
      } else {
        newErrors[cardId - 1].secondaryLossError = !value;
      }
      return newErrors;
    });
  };
  
  // Handles navigation to next tab after validation of current tab details
  const handleContinue = () => {
    let isPrimaryAutocompleteValid = !primaryLossError;
    let isSecondaryAutocompleteValid = !secondaryLossError;
    let isRiskScenarioFilled = selectedRiskScenario !== "";
    let isPrimaryLossFilled = lossCards.every(
      (card) => card.primaryLoss !== ""
    );
    let isSecondaryLossFilled = lossCards.every(
      (card) => card.secondaryLoss !== ""
    );
    let isMinPLossFilled = !lossCards.some((card) => card.minploss === "");
    let isMaxPLossFilled = !lossCards.some((card) => card.maxploss === "");
    let isMinSLossFilled = !lossCards.some((card) => card.minsloss === "");
    let isMaxSLossFilled = !lossCards.some((card) => card.maxsloss === "");
    let isPrimaryLossValid = true;
    let isSecondaryLossValid = true;
    let isValid = true;
    let errorMessage = "";
    lossCards.forEach((card, index) => {
      if (
        !card.primaryLoss ||
        !card.secondaryLoss ||
        !card.minploss ||
        !card.maxploss ||
        !card.minsloss ||
        !card.maxsloss
      ) {
        isValid = false;
        errorMessage = `All fields are required for Card ${index + 1}`;
      }
    });
    if (!isValid) {
      openNotification(errorMessage, "error");
      return;
    }
    lossCards.forEach((card) => {
      if (!card.primaryLoss) {
        isPrimaryLossValid = false;
      }
    });
    lossCards.forEach((card) => {
      if (!card.secondaryLoss) {
        isSecondaryLossValid = false;
      }
    });
    if (!isRiskScenarioFilled) {
      openNotification("Risk scenario is not selected.");
    }
    if (!isPrimaryLossFilled) {
      openNotification("Primary loss is not filled for all cards.");
    }
    if (!isSecondaryLossFilled) {
      openNotification("Secondary loss is not filled for all cards.");
    }
    if (!isMinPLossFilled) {
      openNotification("Min primary loss is not filled for at least one card.");
    }
    if (!isMaxPLossFilled) {
      openNotification("Max primary loss is not filled for at least one card.");
    }
    if (!isMinSLossFilled) {
      openNotification( "Min secondary loss is not filled for at least one card.");
    }
    if (!isMaxSLossFilled) {
      openNotification("Min secondary loss is not filled for at least one card.");
    }
    if (!isPrimaryLossValid) {
      openNotification("Primary loss is not valid for at least one card.");
    }
    if (!isSecondaryLossValid) {
      openNotification("Secondary loss is not valid for at least one card.");
    }
    if (
      isPrimaryLossValid &&
      isSecondaryLossValid &&
      isRiskScenarioFilled &&
      isPrimaryLossFilled &&
      isSecondaryLossFilled &&
      isMinPLossFilled &&
      isMaxPLossFilled &&
      isMinSLossFilled &&
      isPrimaryAutocompleteValid &&
      isSecondaryAutocompleteValid &&
      isMaxSLossFilled
    ) {
      setSelectedTab("2");
    } 
  };
  
  // Handles navigation to previous tab 
  const handlePrevious = () => {
    let prevTab;
    switch (selectedTab) {
      case "2":
        prevTab = "1";
        break;
      case "3":
        prevTab = "2";
        break;
      default:
        prevTab = "1";
        break;
    }
    setSelectedTab(prevTab);
  };
  
  // Handles validation for frequency
  const validateFrequency = () => {
    const minFreq = parseFloat(formData.minfreq);
    const maxFreq = parseFloat(formData.maxfreq);
    const mostLikelyFreq = parseFloat(formData.mof);
    let isValid = true;
    if (
      formData.minfreq === "" ||
      formData.maxfreq === "" ||
      formData.mof === ""
    ) {
      setMinFreqError(formData.minfreq === "");
      setMaxFreqError(formData.maxfreq === "");
      setMostLikelyFreqError(formData.mof === "");
      isValid = false;
    } else {
      if (minFreq > maxFreq) {
        setMinFreqError(true);
        setMaxFreqError(true);
        isValid = false;
      } else {
        setMinFreqError(false);
      }

      if (mostLikelyFreq < minFreq || mostLikelyFreq > maxFreq) {
        setMostLikelyFreqError(true);
        isValid = false;
      } else {
        setMostLikelyFreqError(false);
      }
    }

    return isValid;
  };
  
  // Handles navigation to last tab if the frequency validation is satisfied
  const handleContinueFrequency = () => {
    const isValid = validateFrequency();
    if (isValid) {
      setSelectedTab("3");
    } 
  };
  
  // Handles minimum frequency errors
  const handleMinFreqErrors = () => {
    if (formData.minfreq === "") {
      setMinFreqError(true);
    } else {
      setMinFreqError(false);
    }
  };
  
  // Handles maximum frequency errors
  const handleMaxFreqErrors = () => {
    if (formData.maxfreq === "") {
      setMaxFreqError(true);
    } else {
      setMaxFreqError(false);
    }
  };
  
  // Handles most likely frequency errors
  const handleMostLikelyFreqErrors = () => {
    if (formData.mof === "") {
      setMostLikelyFreqError(true);
    } else {
      setMostLikelyFreqError(false);
    }
  };
  
  // Deletes the loss card if user clicks on delete icon
  const deleteLoss  = (cardId) => {
    setLossCards(prevLossCards => {
      const newLossCards = prevLossCards.filter(card => card.id !== cardId);
      return newLossCards.map((card, index) => ({ ...card, id: index + 1 }));
    });
    
    setLossCardsErrors(prevErrors => {
      const newErrors = prevErrors.filter((error, index) => index !== cardId - 1);
      return newErrors.map((error, index) => ({
        ...error,
        id: index + 1,
        minPLossError: false,
        minPLossValueError: false,
        maxPLossError: false,
        maxPLossValueError: false,
        minSLossError: false,
        minSLossValueError: false,
        maxSLossError: false,
        maxSLossValueError: false,
      }));
    });
  };
  
  // Handles minimum primary loss errors  
  const handleMinPrimaryLossErrors = (event, cardId) => {
    const value = event.target.value
      .replace(/\D/g, "")
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    setLossCards((prevLossCards) => {
      const newLossCards = [...prevLossCards];
      const index = cardId - 1;

      // Check if the card exists
      if (index >= 0 && index < newLossCards.length) {
        newLossCards[index].minploss = value;
        const minploss = parseFloat(value.replace(/,/g, ""));
        const maxploss = parseFloat(
          newLossCards[index].maxploss.replace(/,/g, "")
        );

        // Update error states
        setLossCardsErrors((prevErrors) => {
          const newErrors = [...prevErrors];
          if (isNaN(minploss)) {
            newErrors[index].minPLossError = true;
          } else if (minploss > maxploss) {
            newErrors[index].minPLossValueError = true;
            newErrors[index].maxPLossValueError = true;
          } else {
            newErrors[index].minPLossError = false;
            newErrors[index].maxPLossError = false;
            newErrors[index].minPLossValueError = false;
            newErrors[index].maxPLossValueError = false;
          }
          return newErrors;
        });
      }

      return newLossCards;
    });
  };
  
  // Handles maximum primary loss errors
  const handleMaxPrimaryLossErrors = (event, cardId) => {
    const value = event.target.value
      .replace(/\D/g, "")
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      setLossCards((prevLossCards) => {
      const newLossCards = [...prevLossCards];
      const index = cardId - 1;

      // Check if the card exists
      if (index >= 0 && index < newLossCards.length) {
        newLossCards[index].maxploss = value;
        const maxploss = parseFloat(value.replace(/,/g, ""));
        const minploss = parseFloat(
          newLossCards[index].minploss.replace(/,/g, "")
        );

        // Update error states
        setLossCardsErrors((prevErrors) => {
          const newErrors = [...prevErrors];
          if (isNaN(maxploss)) {
            newErrors[index].maxPLossError = true;
          } else if (maxploss < minploss) {
            newErrors[index].maxPLossValueError = true;
            newErrors[index].minPLossValueError = true;
          } else {
            newErrors[index].maxPLossError = false;
            newErrors[index].minPLossError = false;
            newErrors[index].maxPLossValueError = false;
            newErrors[index].minPLossValueError = false;
          }
          return newErrors;
        });
      }

      return newLossCards;
    });
  };
  
  // Handles minimum secondary loss errors
  const handleMinSecondaryLossErrors = (event, cardId) => {
    const value = event.target.value.trim();
    setLossCards((prevLossCards) => {
      const newLossCards = [...prevLossCards];
      newLossCards[cardId - 1].minsloss = value;
      if (value === "") {
        setLossCardsErrors((prevErrors) => {
          const newErrors = [...prevErrors];
          newErrors[cardId - 1].minSLossError = true;
          return newErrors;
        });
      }
      return newLossCards;
    });
  };
  
  // Handles maximum secondary loss errors
  const handleMaxSecondaryLossErrors = (event, cardId) => {
    const value = event.target.value.trim();
    setLossCards((prevLossCards) => {
      const newLossCards = [...prevLossCards];
      newLossCards[cardId - 1].maxsloss = value;
      if (value === "") {
        setLossCardsErrors((prevErrors) => {
          const newErrors = [...prevErrors];
          newErrors[cardId - 1].maxSLossError = true;
          return newErrors;
        });
      }
      return newLossCards;
    });
  };

  return (
    <div className="p-4">
      <h5 className="curve-heading mb-3">Risk parameters</h5>
      <div className="card">
        <div>
          <BootstrapTabs activeKey={selectedTab} onSelect={(key) => setSelectedTab(key)} justify className="custom-tab-styles">
            <BootstrapTab eventKey="1" title="Loss Severity">
              <div className="p-4">
                <p className="risk-scernario">Risk scenario</p>
                <div className="autocomplete-2">
                  <Autocomplete
                    disablePortal
                    options={riskScenarios}
                    getOptionLabel={(option) =>
                      option && option.RiskScenarios ? option.RiskScenarios : ""
                    }
                    value={selectedRiskScenario}
                    onChange={(event,value) => setSelectedRiskScenario(value)}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </div>
                <div className="header-cont mt-3">
                  <div>
                    <p className="risk-scernario">Loss category</p>
                  </div>
                  <div>
                    <Button variant="contained" onClick={addLossCard}>
                      {" "}
                      + ADD
                    </Button>
                  </div>
                </div>
                <Divider className="divider mb-3" />
                {lossCards.map((card, index) => (
                  <div className="card p-4 mt-3 card-bg full-width-field" key={index}>
                    {lossCards.length > 1 && (
                      <div className="bin-container">
                        <DeleteIcon onClick={() => deleteLoss(card.id)} />
                      </div>
                    )}
                    <div className="priamry-card">
                      <div className="p-card-1">
                        <p>Primary loss</p>
                        <div className="priamry-card">
                          <div className="autocomplete-1 m-1">
                            <Autocomplete
                              id={`primary-loss-autocomplete-${index}`}
                              options={riskScenarioPrimaryLosses.map(
                                (option) => option.risk_loss
                              )}
                              value={card.primaryLoss}
                              onChange={(event, value) =>
                                handlePrimaryLossChange(event, value, card.id)
                              }
                              onBlur={() =>
                                handleFieldErrors(
                                  card.primaryLoss,
                                  setPrimaryLossError,
                                  card.id,
                                  true
                                )
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Primary Loss"
                                  helperText={
                                    lossCardsErrors[card.id - 1]
                                      .primaryLossError
                                      ? "Field is required"
                                      : ""
                                  }
                                  FormHelperTextProps={{
                                    sx: {
                                      color: lossCardsErrors[card.id - 1]
                                        .primaryLossError
                                        ? "red"
                                        : "inherit",
                                    },
                                  }}
                                />
                              )}
                            />
                          </div>
                          <div className="m-3 add-conatiner">
                            {" "}
                            <AddBoxIcon
                              onClick={() => setPrimaryPopupOpen(true)}
                            />
                          </div>
                        </div>
                        <div className="priamry-card mt-3">
                          <div className="m-3">
                            <FormControl error={lossCardsErrors[index].maxPLossError}>
                              <InputLabel
                                className="Risk-1"
                                htmlFor="outlined-adornment-amount"
                              >
                                Max loss
                              </InputLabel>
                              <OutlinedInput
                                startAdornment={
                                  <InputAdornment position="start">
                                    $
                                  </InputAdornment>
                                }
                                label="Amount"
                                value={card.maxploss}
                                onChange={(event) =>
                                  handleMaxPrimaryLossChange(event, card.id)
                                }
                                onBlur={(event) =>
                                  handleMaxPrimaryLossErrors(event, card.id)
                                }
                                helperText={
                                  lossCardsErrors[card.id - 1].maxPLossError
                                    ? "Field is required"
                                    : ""
                                }
                                FormHelperTextProps={{
                                  sx: {
                                    color: lossCardsErrors[card.id - 1]
                                      .maxPLossError
                                      ? "red"
                                      : "inherit",
                                  },
                                }}
                              />
                              {lossCardsErrors[index].maxPLossError && (
                                <FormHelperText>
                                  Field is required
                                </FormHelperText>
                              )}
                            </FormControl>
                          </div>
                          <div className="m-3">
                            <FormControl error={lossCardsErrors[index].minPLossError}>
                              <InputLabel
                                className="Risk-1"
                                htmlFor="outlined-adornment-amount"
                              >
                                Min Loss
                              </InputLabel>
                              <OutlinedInput
                                startAdornment={
                                  <InputAdornment position="start">
                                    $
                                  </InputAdornment>
                                }
                                label="Amount"
                                value={card.minploss}
                                onChange={(event) =>
                                  handleMinPrimaryLossChange(event, card.id)
                                }
                                onBlur={(event) =>
                                  handleMinPrimaryLossErrors(event, card.id)
                                }
                                helperText={
                                  lossCardsErrors[card.id - 1].minPLossError
                                    ? "Field is required"
                                    : ""
                                }
                                FormHelperTextProps={{
                                  sx: {
                                    color: lossCardsErrors[card.id - 1]
                                      .minPLossError
                                      ? "red"
                                      : "inherit",
                                  },
                                }}
                              />
                              {lossCardsErrors[index].minPLossError && (
                                <FormHelperText>
                                  Field is required
                                </FormHelperText>
                              )}
                            </FormControl>
                          </div>
                        </div>
                        <div className="btn-container">
                          <div>
                            {lossCardsErrors[index].minPLossValueError && (
                              <FormHelperText className="crq-error-message">
                                Min loss should not be greater than max loss
                              </FormHelperText>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="p-card-1">
                        <p>Secondary Loss</p>
                        <div className="priamry-card">
                          <div className="autocomplete-1 m-1">
                            <Autocomplete
                              id={`secondary-loss-autocomplete-${index}`}
                              options={riskScenarioSecondaryLosses.map(
                                (option) => option.risk_loss
                              )}
                              value={card.secondaryLoss}
                              onChange={(event, value) =>
                                handleSecondaryLossChange(event, value, card.id)
                              }
                              onBlur={() =>
                                handleFieldErrors(
                                  card.secondaryLoss,
                                  setSecondaryLossError,
                                  card.id,
                                  false
                                )
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Secondary Loss"
                                  helperText={
                                    lossCardsErrors[card.id - 1]
                                      .secondaryLossError
                                      ? "Field is required"
                                      : ""
                                  }
                                  FormHelperTextProps={{
                                    sx: {
                                      color: lossCardsErrors[card.id - 1]
                                        .secondaryLossError
                                        ? "red"
                                        : "inherit",
                                    },
                                  }}
                                />
                              )}
                            />
                          </div>
                          <div className="m-3 add-conatiner">
                            <AddBoxIcon
                              onClick={() => setSecondaryPopupOpen(true)}
                            />
                          </div>
                        </div>
                        <div className="priamry-card mt-3">
                          <div className="m-3">
                            <FormControl
                              error={lossCardsErrors[index].maxSLossError}
                            >
                              <InputLabel
                                className="Risk-1"
                                htmlFor="outlined-adornment-amount"
                              >
                                Max Loss
                              </InputLabel>
                              <OutlinedInput
                                startAdornment={
                                  <InputAdornment position="start">
                                    $
                                  </InputAdornment>
                                }
                                label="Amount"
                                value={card.maxsloss}
                                onChange={(event) =>
                                  handleMaxSecondaryLossChange(event, card.id)
                                }
                                onBlur={(event) =>
                                  handleMaxSecondaryLossErrors(event, card.id)
                                }
                                helperText={
                                  lossCardsErrors[card.id - 1].maxSLossError
                                    ? "Field is required"
                                    : ""
                                }
                                FormHelperTextProps={{
                                  sx: {
                                    color: lossCardsErrors[card.id - 1]
                                      .maxSLossError
                                      ? "red"
                                      : "inherit",
                                  },
                                }}
                              />
                              {lossCardsErrors[index].maxSLossError && (
                                <FormHelperText>
                                  Field is required
                                </FormHelperText>
                              )}
                            </FormControl>
                          </div>
                          <div className="m-3">
                            <FormControl error={lossCardsErrors[index].minSLossError}>
                              <InputLabel
                                className="Risk-1"
                                htmlFor="outlined-adornment-amount"
                              >
                                Min Loss
                              </InputLabel>
                              <OutlinedInput
                                startAdornment={
                                  <InputAdornment position="start">
                                    $
                                  </InputAdornment>
                                }
                                label="Amount"
                                value={card.minsloss}
                                onChange={(event) =>
                                  handleMinSecondaryLossChange(event, card.id)
                                }
                                onBlur={(event) =>
                                  handleMinSecondaryLossErrors(event, card.id)
                                }
                                helperText={
                                  lossCardsErrors[card.id - 1].minSLossError
                                    ? "Field is required"
                                    : ""
                                }
                                FormHelperTextProps={{
                                  sx: {
                                    color: lossCardsErrors[card.id - 1]
                                      .minSLossError
                                      ? "red"
                                      : "inherit",
                                  },
                                }}
                              />
                              {lossCardsErrors[index].minSLossError && (
                                <FormHelperText>
                                  Field is required
                                </FormHelperText>
                              )}
                            </FormControl>
                          </div>
                        </div>
                        <div className="btn-container">
                          <div>
                            {lossCardsErrors[index].minSLossValueError && (
                              <FormHelperText className="crq-error-message">
                                Min loss should not be greater than max loss
                              </FormHelperText>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="standards-container m-3">
                  <Button onClick={handleContinue} variant="contained">
                    Continue
                  </Button>
                </div>
              </div>
            </BootstrapTab>
            <BootstrapTab eventKey="2" title="Frequency" disabled>
              <div className="p-4">
                <div className="card p-4 mt-3 card-bg full-width-field">
                  <p className="risk-scernario">Frequency</p>
                  <hr />
                  <div className="priamry-card mt-4">
                    <div className="outline-card m-2">
                      <TextField
                        label="Minimum Frequency"
                        variant="outlined"
                        fullWidth
                        value={formData.minfreq}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setFormData((prevFormData) => ({
                            ...prevFormData,
                            minfreq: newValue,
                          }));
                          const minFreq = parseFloat(newValue);
                          const maxFreq = parseFloat(formData.maxfreq);
                          const isValid =
                            !isNaN(minFreq) &&
                            !isNaN(maxFreq) &&
                            minFreq <= maxFreq;
                          setMinFreqError(!isValid);
                        }}
                        onBlur={handleMinFreqErrors}
                        error={minFreqError}
                        helperText={
                          minFreqError
                            ? formData.minfreq === ""
                              ? "Field is required"
                              : "Minimum Frequency should be less than Maximum Frequency"
                            : ""
                        }
                      />
                    </div>
                    <div className="outline-card m-2">
                      <TextField
                        label="Max Frequency"
                        variant="outlined"
                        fullWidth
                        value={formData.maxfreq}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setFormData((prevFormData) => ({
                            ...prevFormData,
                            maxfreq: newValue,
                          }));
                          const minFreq = parseFloat(formData.minfreq);
                          const maxFreq = parseFloat(newValue);
                          const isValid =
                            !isNaN(minFreq) &&
                            !isNaN(maxFreq) &&
                            minFreq <= maxFreq;
                          setMaxFreqError(!isValid);
                        }}
                        onBlur={handleMaxFreqErrors}
                        error={maxFreqError}
                        helperText={
                          maxFreqError
                            ? formData.maxfreq === ""
                              ? "Field is required"
                              : "Max Frequency should be greater than Minimum Frequency"
                            : ""
                        }
                      />
                    </div>
                    <div className="outline-card m-2">
                      <TextField
                        label="Most Likely Frequency"
                        variant="outlined"
                        fullWidth
                        value={formData.mof}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setFormData((prevFormData) => ({
                            ...prevFormData,
                            mof: newValue,
                          }));
                          const minFreq = parseFloat(formData.minfreq);
                          const maxFreq = parseFloat(formData.maxfreq);
                          const mostLikelyFreq = parseFloat(newValue);
                          const isValid =
                            !isNaN(mostLikelyFreq) &&
                            minFreq <= mostLikelyFreq &&
                            mostLikelyFreq <= maxFreq;
                          setMostLikelyFreqError(!isValid);
                        }}
                        onBlur={handleMostLikelyFreqErrors}
                        error={mostLikelyFreqError}
                        helperText={
                          mostLikelyFreqError
                            ? formData.mof === ""
                              ? "Field is required"
                              : "Most Likely Frequency should be between Minimum and Maximum Frequency"
                            : ""
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="standards-container m-3">
                  <Button
                    onClick={handlePrevious}
                    variant="contained"
                    className="prev-button m-2"
                  >
                    Previous
                  </Button>
                  <Button
                    onClick={handleContinueFrequency}
                    variant="contained"
                    className="m-2"
                  >
                    Continue
                  </Button>
                </div>{" "}
              </div>
            </BootstrapTab>
            <BootstrapTab eventKey="3" title="Control Strength" disabled>
              <div className="p-4">
                <div className="card p-4 mt-3 card-bg">
                  <p className="risk-scernario">Resistance strength </p>
                  <hr />
                  <div className="priamry-card">
                    <div className="outline-card m-3">
                      <TextField
                        label="Control Strength"
                        value={controlStrength}
                        onChange={(e) => setControlStrength(e.target.value)}
                        onBlur={handleControlStrength}
                        error={controlStrengthError}
                        helperText={controlStrengthError && "Filed Is required"}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="standards-container m-3">
                  <Button
                    onClick={handlePrevious}
                    variant="contained"
                    className="prev-button m-2"
                  >
                    Previous
                  </Button>
                  <Button
                    onClick={handleAnalysis}
                    variant="contained"
                    className="m-2"
                  >
                    Submit
                  </Button>
                </div>
              </div>
            </BootstrapTab>
          </BootstrapTabs>
        </div>
      </div>
      <div className="p-4">
        <Dialog
          open={primaryPopupOpen}
          onClose={() => setPrimaryPopupOpen(false)}
        >
          <DialogTitle>Add primary loss</DialogTitle>
          <Divider className="divider" />
          <DialogContent>
            <TextField
              className="crq-textfiled mt-3"
              label="Primary Loss Type"
              value={newPrimaryLossType}
              onChange={(e) => setNewPrimaryLossType(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              className="cancel-btn m-3"
              onClick={() => setPrimaryPopupOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className="Add-btn m-3"
              onClick={handleAddPrimaryLoss}
            >
              Add
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={secondaryPopupOpen}
          onClose={() => setSecondaryPopupOpen(false)}
        >
          <DialogTitle>Add secondary loss</DialogTitle>
          <Divider className="divider" />
          <DialogContent>
            <TextField
              className="crq-textfiled mt-3 "
              label="Secondary Loss Type"
              value={newSecondaryLossType}
              onChange={(e) => setNewSecondaryLossType(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              className="cancel-btn m-3"
              onClick={() => setSecondaryPopupOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className="Add-btn m-3"
              onClick={handleAddSecondaryLoss}
            >
              Add
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};
export default RiskQuantPage;